/**
 * ShareDialog — create a view-only link for the current analysis.
 */
import { useState, useCallback } from 'react';
import { ShareNetwork, CopySimple, Check, X } from '@phosphor-icons/react';
import { createShareSnapshot, absoluteShareUrl } from '@/utils/shareApi';

export function ShareDialog({
  open,
  onClose,
  name,
  dealId,
  inputs,
  assumptions,
  notes,
  cadastreData,
  marketData,
  fxSnapshot,
  landAssessment = null,
  activeScenarioId = null,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setError('');
    setCopied(false);
    try {
      const result = await createShareSnapshot({
        name: name || 'Shared analysis',
        dealId: dealId || undefined,
        inputs,
        assumptions,
        notes: notes || '',
        cadastreData: cadastreData || null,
        marketData: marketData || null,
        fxSnapshot: fxSnapshot || null,
        landAssessment: landAssessment || null,
        activeScenarioId: activeScenarioId || null,
      });
      if (!result?.path) {
        setError('Could not create share link. Try again.');
        return;
      }
      setLink(absoluteShareUrl(result.path));
    } catch {
      setError('Could not create share link. Try again.');
    } finally {
      setBusy(false);
    }
  }, [name, dealId, inputs, assumptions, notes, cadastreData, marketData, fxSnapshot, landAssessment, activeScenarioId]);

  const handleCopy = useCallback(async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed — select the link and copy manually.');
    }
  }, [link]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/40 flex items-center justify-center p-3"
      data-testid="share-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-dialog-title"
    >
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 shadow-xl p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h2 id="share-dialog-title" className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              <ShareNetwork weight="duotone" className="w-4 h-4 text-indigo-600" />
              Share analysis
            </h2>
            <p className="text-[11px] text-slate-500 mt-1 leading-snug">
              Recipients open a <span className="font-semibold">view-only</span> copy of the full analysis
              (inputs, sheet, notes, market range). They cannot edit or save.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded"
            aria-label="Close share dialog"
            data-testid="share-dialog-close"
          >
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>

        {!link ? (
          <div className="space-y-3">
            <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2.5 py-2">
              Snapshot: <span className="font-semibold">{name || 'Untitled analysis'}</span>
            </div>
            {error && (
              <div className="text-[11px] text-red-600" data-testid="share-dialog-error">{error}</div>
            )}
            <button
              type="button"
              onClick={handleCreate}
              disabled={busy}
              className="w-full h-9 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-[12px] font-semibold"
              data-testid="share-create-btn"
            >
              {busy ? 'Creating link…' : 'Create view-only link'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-[10px] font-semibold uppercase text-slate-500" htmlFor="share-link-input">
              View-only link
            </label>
            <input
              id="share-link-input"
              readOnly
              value={link}
              className="w-full text-[11px] border border-indigo-200 rounded px-2 py-2 bg-indigo-50/50 text-indigo-900"
              data-testid="share-link-input"
              onFocus={(e) => e.target.select()}
            />
            {error && (
              <div className="text-[11px] text-red-600" data-testid="share-dialog-error">{error}</div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="flex-1 h-9 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[12px] font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="share-copy-btn"
              >
                {copied ? <Check weight="bold" className="w-3.5 h-3.5" /> : <CopySimple weight="bold" className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="h-9 px-3 rounded-md border border-slate-200 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
